import { type RestrictionStage } from "@clipboard-health/contract-payment-service";
import { isDefined } from "@clipboard-health/util-ts";
import { type Theme } from "@mui/material/styles";
import { type IconType, PromotionBanner } from "@src/appV2/redesign/system/ui-components";

interface StageConfig {
  backgroundColor: (theme: Theme) => string;
  titleColor: (theme: Theme) => string;
  icon: IconType;
}

const stageConfigMap: Record<RestrictionStage, StageConfig> = {
  error: {
    backgroundColor: (theme) => theme.palette.intent!.error.gradient!,
    titleColor: (theme) => theme.palette.intent!.error.text,
    icon: "dollar-error-colored",
  },
  warn: {
    backgroundColor: (theme) => theme.palette.intent!.warning.gradient!,
    titleColor: (theme) => theme.palette.intent!.warning.text,
    icon: "dollar-warning-colored",
  },
  other: {
    backgroundColor: (theme) => theme.palette.intent!.warning.gradient!,
    titleColor: (theme) => theme.palette.intent!.warning.text,
    icon: "dollar-warning-colored",
  },
  info: {
    backgroundColor: (theme) => theme.palette.intent!.neutral.gradient!,
    titleColor: (theme) => theme.palette.intent!.neutral.text,
    icon: "dollar-info-colored",
  },
};

function formatDeadline(deadlineInSeconds: number): string {
  const date = new Date(deadlineInSeconds * 1000);
  if (date.getUTCFullYear() === new Date().getUTCFullYear()) {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

interface StripeRestrictionBannerProps {
  stage: RestrictionStage;
  message: string;
  deadlineInSeconds?: number;
  isLoading: boolean;
  onClick: () => void;
}

export function StripeRestrictionBanner(props: StripeRestrictionBannerProps) {
  const { stage, message, deadlineInSeconds, isLoading, onClick } = props;

  const config = stageConfigMap[stage];

  const title = isDefined(deadlineInSeconds)
    ? `Your Stripe account needs attention by ${formatDeadline(deadlineInSeconds)}`
    : "Your Stripe account needs attention";

  return (
    <PromotionBanner
      title={title}
      subtitle={message}
      icon={config.icon}
      borderVariant="none"
      backgroundColor={config.backgroundColor}
      titleColor={config.titleColor}
      ctaButtonText="Go to Stripe"
      ctaButtonVariant="outlined"
      ctaButtonIsLoading={isLoading}
      sx={{
        height: "100%",
        "& .MuiButtonBase-root": {
          border: "none",
          boxShadow: "none",
        },
      }}
      onClick={onClick}
    />
  );
}
